import type {
  DivisionsTree,
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  ISchedule,
  Office,
  OfficeWithFloors
} from "@gettactic/api";
import { OfficeItem } from "../search-result-items/OfficeItem";
import { PersonItem } from "../search-result-items/PersonItem";
import { ResourceItem } from "../search-result-items/ResourceItem";
import { SettingItem } from "../search-result-items/SettingItem";
import type { Setting } from "../constants";
import { SearchSection } from "./SearchSection";

interface SearchResultsProps {
  filteredActions: Setting[];
  resources: GlobalSearchResourceItem[];
  people: GlobalSearchUserResult[];
  filteredOffices: Office[];
  filteredSettings: Setting[];
  schedulesByResource: Record<string, ISchedule[]>;
  schedulesByUser: Record<string, ISchedule[]>;
  divisionCategories?: {
    offices: Record<string, OfficeWithFloors>;
    tree: DivisionsTree | null;
  };
  activeIndex: number;
  registerItemRef: (index: number) => (el: HTMLDivElement | null) => void;
  onNavigateToResource: (resourceId: string) => void;
  onNavigateToPerson: (person: GlobalSearchUserResult, schedules: ISchedule[]) => void;
  onNavigateToOffice: (officeId: string) => void;
  onEditOffice: (officeId: string) => void;
  onReserveOffice: (officeId: string) => void;
  onSelectSetting: (setting: Setting) => void;
}

export function SearchResults({
  filteredActions,
  resources,
  people,
  filteredOffices,
  filteredSettings,
  schedulesByResource,
  schedulesByUser,
  divisionCategories,
  activeIndex,
  registerItemRef,
  onNavigateToResource,
  onNavigateToPerson,
  onNavigateToOffice,
  onEditOffice,
  onReserveOffice,
  onSelectSetting
}: SearchResultsProps) {
  let currentIndex = 0;

  return (
    <div className="space-y-4">
      {filteredActions.length > 0 && (
        <SearchSection title="Quick Actions">
          {filteredActions.map((setting) => {
            const index = currentIndex++;
            return (
              <div key={setting.id} ref={registerItemRef(index)}>
                <SettingItem
                  setting={setting}
                  isActive={index === activeIndex}
                  onSelect={() => onSelectSetting(setting)}
                />
              </div>
            );
          })}
        </SearchSection>
      )}

      {resources.length > 0 && (
        <SearchSection title="Resources">
          {resources.map((resource) => {
            const index = currentIndex++;
            return (
              <div key={resource.resource.id} ref={registerItemRef(index)}>
                <ResourceItem
                  resource={resource}
                  schedules={schedulesByResource[resource.resource.id] || []}
                  isActive={index === activeIndex}
                  onSelect={() => onNavigateToResource(resource.resource.id)}
                  divisionCategories={divisionCategories}
                />
              </div>
            );
          })}
        </SearchSection>
      )}

      {people.length > 0 && (
        <SearchSection title="People">
          {people.map((person) => {
            const index = currentIndex++;
            const schedules = schedulesByUser[person.id] || [];

            return (
              <div key={person.id} ref={registerItemRef(index)}>
                <PersonItem
                  person={person}
                  schedules={schedules}
                  isActive={index === activeIndex}
                  onSelect={() => onNavigateToPerson(person, schedules)}
                  divisionCategories={divisionCategories}
                />
              </div>
            );
          })}
        </SearchSection>
      )}

      {filteredOffices.length > 0 && (
        <SearchSection title="Offices">
          {filteredOffices.map((office) => {
            const index = currentIndex++;
            return (
              <div key={office.id} ref={registerItemRef(index)}>
                <OfficeItem
                  office={office}
                  isActive={index === activeIndex}
                  onSelect={() => onNavigateToOffice(office.id)}
                  onEdit={() => onEditOffice(office.id)}
                  onReserve={() => onReserveOffice(office.id)}
                />
              </div>
            );
          })}
        </SearchSection>
      )}

      {filteredSettings.length > 0 && (
        <SearchSection title="Settings">
          {filteredSettings.map((setting) => {
            const index = currentIndex++;
            return (
              <div key={setting.id} ref={registerItemRef(index)}>
                <SettingItem
                  setting={setting}
                  isActive={index === activeIndex}
                  onSelect={() => onSelectSetting(setting)}
                />
              </div>
            );
          })}
        </SearchSection>
      )}
    </div>
  );
}
